import { useState } from "react"

import { Box } from "@mui/material"
import { alpha } from "@mui/material/styles"

import { IconButton, SvgIcon, type FC } from ".."
import type { IconButtonProps } from "../Button/IconButton"

type WishlistButtonProps = Omit<IconButtonProps, "icon"> & {
  liked?: boolean | null
  onLike?: () => void
  onUnlike?: () => void
}

const WishlistButton: FC<WishlistButtonProps> = ({
  sx,
  liked = null,
  onLike = () => {},
  onUnlike = () => {},
  ...props
}) => {
  const [isAnimating, setIsAnimating] = useState(false)
  const onClick = () => {
    if (liked) {
      onUnlike()
      setIsAnimating(false)
    } else {
      onLike()
      setIsAnimating(true)
    }
  }

  return (
    <Box position="relative">
      <IconButton
        variant="base"
        aria-label="Add to Wishlist"
        onClick={onClick}
        icon={
          <SvgIcon
            variant="heart"
            strokeMode
            size={18}
            sx={(theme) => ({
              "@keyframes like": {
                "0%": {
                  transform: "scale(1)",
                  fill: alpha(theme.palette["primary.800"].main, 0),
                },

                "50%": {
                  transform: "scale(1.2)",
                  fill: alpha(theme.palette["primary.800"].main, 0),
                },

                "65%": {
                  transform: "scale(1.2)",
                  fill: theme.palette["primary.800"].main,
                },

                "100%": {
                  transform: "scale(1)",
                  fill: theme.palette["primary.800"].main,
                  stroke: theme.palette["primary.800"].main,
                },
              },

              "@keyframes unlike": {
                "0%": {
                  transform: "scale(1)",
                  fill: theme.palette["neutral.100"].main,
                },

                "50%": {
                  transform: "scale(1.2)",
                  fill: theme.palette["neutral.100"].main,
                },

                "65%": {
                  transform: "scale(1.2)",
                  fill: alpha(theme.palette["neutral.100"].main, 0),
                },

                "100%": {
                  transform: "scale(1)",
                  fill: alpha(theme.palette["neutral.100"].main, 0),
                },
              },
              "stroke": theme.palette["neutral.100"].main,
              "strokeWidth": 2,
              ...(liked !== null && {
                animation: `${liked ? "like" : "unlike"} 500ms ease-in-out forwards`,
              }),
              "fill": "none",
            })}
          />
        }
        sx={[
          (theme) => ({
            width: { xs: 18 },
            height: { xs: 18 },
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...props}
      />
      <Box
        sx={(theme) => ({
          "@keyframes pulse": {
            "0%": {
              opacity: 1,
              transform: "translateY(0)",
            },
            "50%": {
              opacity: 0,
              transform: "translateY(-20px)",
            },
            "100%": {
              opacity: 0,
              transform: "translateY(-40px)",
            },
          },
          "position": "absolute",
          "top": -15,
          "right": 0,
          "opacity": 0,
          "color": theme.palette["neutral.100"].main,
          ...(isAnimating && {
            animation: `pulse 500ms ease-in-out forwards`,
          }),
        })}
      >
        +1
      </Box>
    </Box>
  )
}
export default WishlistButton
